import { createEffect, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { convertSizeUnit } from "../js/utils";
import { InlineUnitSwitch, UnitSwitch } from "./UnitSwitch";
import { useHeroForm } from "../contexts/HeroFormContext";
import { Modal, ModalV2 } from "./Modal";
import { AddToBagButton } from "../containers/AddToBagButton";
import { Picture } from "./Picture";
import { InfoIcon } from "./InfoIcon";
import { NeumorphicCustomizationButton } from "./NeumorphicOptionButton";

const ContextualSizeChart = ({
  headers,
  selectedUnit,
  setSelectedUnit,
  originalUnit,
  fitdata,
}) => {
  const {
    sizeSettings: {
      standardSizeData,
      handlers: { handleAnchorGradedChange },
    },
    fit: { fit },
  } = useHeroForm();

  const roundToClosest = (num) => {
    return (
      selectedUnit() == "cm" ? Math.round(num * 2) / 2 : Math.round(num * 4) / 4
    ).toFixed(1);
  };

  const selectedSizeData = () => {
    const selectedId = Object.values(
      standardSizeData().measurements.anchorGraded
    ).shift().selected;
    const columnHeader = headers.rows
      .filter((row) => row.id === selectedId)
      .shift();
    const measurements = fitdata[fit().selected].sizes[selectedId].measurements;
    const rows = headers.columns.map((column) => {
      return { measurement: measurements[column.id], ...column };
    });
    return {
      columnHeader: columnHeader,
      rows: rows,
    };
  };
  return (
    <div class="">
      <span class="font-medium w-full text-center block pb-1">Size Guide </span>
      <div class="grid grid-cols-2 items-start">
        <div class="">
          <div class="border-frame border-outer grid grid-cols-3">
            <div class="border-frame border-r-inner p-1 col-span-2">
              <UnitSwitch
                unit={selectedUnit}
                setUnit={setSelectedUnit}
                buttonClass="!border-outer border-frame border-solid rounded-full items-center w-[34px] h-[22px] bg-secondary-300"
                buttonContentClass="w-[14px] h-[14px] rounded-full"
              />
            </div>
            <div class="text-base text-center font-bold sm:text-sm py-1">
              {selectedSizeData().columnHeader.label}
            </div>
            <For each={selectedSizeData().rows}>
              {(row) => (
                <>
                  <div class="text-xs text-left sm:text-sm pl-1 py-1 col-span-2 border-frame border-r-inner border-t-inner">
                    {row.label}
                  </div>
                  <div class="text-xs text-center sm:text-sm py-1 border-frame border-t-inner">
                    {roundToClosest(
                      convertSizeUnit(
                        originalUnit,
                        selectedUnit(),
                        row.measurement.value
                      )
                    )}{" "}
                    {selectedUnit()}
                  </div>
                </>
              )}
            </For>
          </div>
        </div>
        <div>
          <Show when={standardSizeData().sizeGuide.diagram_url != ''}>
            <Picture
              imageUrl={standardSizeData().sizeGuide.diagram_url}
              style='background-image: none;'
              sizeList={[
                {
                  pageWidth: 320,
                  imageWidth: 144,
                },
                {
                  pageWidth: 375,
                  imageWidth: 172,
                },
                {
                  pageWidth: 425,
                  imageWidth: 196,
                },
                {
                  pageWidth: 768,
                  imageWidth: 368,
                },
              ]}
            />
          </Show>
        </div>
      </div>
    </div>
  );
};

const SizeRadioSelector = (props) => {
  const valueOptions = (measurement) => {
    if (props.type != "nonAnchorUngraded") {
      return Object.entries(measurement.values).map(([key, value]) => {
        return {
          id: key,
          baseSize: value.baseSize,
          displayName: value.displayName,
        };
      });
    } else {
      const min = measurement.values["min"];
      const max = measurement.values["max"];
      const step = measurement.values["step"];
      const result = [];

      for (let i = min; i <= max; i += step) {
        result.push(i);
      }
      return result.map((value) => {
        const fixedValue = parseFloat(parseFloat(`${value}`).toFixed(2));
        return {
          id: handleize(`${fixedValue}`),
          value: fixedValue,
          baseSize: `${fixedValue}`,
          displayName: measurement.displayNameTemplate.replace("{{1}}", value),
        };
      });
    }
  };

  const selectedDisplayName = (measurement) => {
    if (props.type != "nonAnchorUngraded") {
      return measurement.values[measurement.selected].displayName[
        props.fit().selected
      ];
    } else {
      return measurement.selected != ""
        ? measurement.displayNameTemplate.replace(
          "{{1}}",
          measurement.selected.replace("-", ".")
        )
        : "";
    }
  };

  return (
    <For each={props.measurements}>
      {(measurement, index) => (
        <div class="w-full">
          <span className="font-bold">
            {measurement.label !== "" ? measurement.label : "Size"}:{" "}
          </span>
          <span className="text-xs font-regular">
            {selectedDisplayName(measurement)}
          </span>

          <div className="grid grid-cols-5 sm:grid-cols-6 gap-1 flex-wrap">
            <For each={valueOptions(measurement)}>
              {(value) => (
                <div
                  class="bg-canvas-depressed group relative flex cursor-pointer items-stretch justify-center hover:bg-canvas-light border border-solid border-frame p-2 sm:p-2 min-w-max sm:text-sm text-xs text-center text-ink  sm:text-sm font-medium"
                  classList={{
                    "boxy-shadow bg-canvas-light":
                      value.id === measurement.selected,
                  }}
                  onClick={() => props.handleChange(measurement.id, value.id)}
                >
                  {value.baseSize}
                </div>
              )}
            </For>
          </div>
        </div>
      )}
    </For>
  );
};

const FitSelector = (props) => {
  const fit = props.fit;
  const selectedOption = () => props.fit().options[props.fit().selected];

  return (
    <div>
      <span class="font-bold">
        Fit:{" "}
        <span className="text-xs font-regular">{` ${selectedOption().displayName || selectedOption().label}`}</span>
      </span>
      <Show when={Object.values(props.fit().options).length > 1}>
        <div class="flex gap-1">
          <For each={Object.values(props.fit().options)}>
            {(option) => (
              <div
                class="bg-canvas-depressed group relative flex cursor-pointer items-stretch justify-center hover:bg-canvas-light border border-solid border-frame p-2 sm:p-2 min-w-max text-xs text-center text-ink  sm:text-sm font-medium"
                classList={{
                  "boxy-shadow bg-canvas-light":
                    props.fit().selected === option.id,
                }}
                onClick={() => props.handleChange(option.id)}
              >
                {option.displayName || option.label}
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};

const FitSelectorMainForm = (props) => {
  return (
    <div class="px-2">
      <div class="flex items-center justify-between">
        <div>
          <span class="font-bold">Fit: </span>
          <span className="text-xs font-regular">{` ${props.fit().options[props.fit().selected].displayName || props.fit().options[props.fit().selected].label
            }`}</span>
        </div>
        <div
          class="cursor-pointer text-left w-max text-sm text-ink-link hover:text-secondary-400"
          onClick={() => props.handleSizeGuideOpen(true)}
        >
          <InfoIcon />
          &thinsp; Size & Fit Guide
        </div>
      </div>
      <Show when={Object.values(props.fit().options).length > 1}>
        {/* <div class="flex gap-1 mt-2 flex-wrap"> */}
        <div class="grid grid-cols-[repeat(auto-fit,minmax(100px,1fr))] gap-1 mt-2">
          <For each={Object.values(props.fit().options)}>
            {(option) => (
              <NeumorphicCustomizationButton
                id={option.id}
                onClick={() => props.handleChange(option.id)}
                selected={props.fit().selected === option.id}
                label={option.displayName || option.label}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
};

const SizeRadioSelectorMainForm = (props) => {
  const valueOptions = (measurement) => {
    if (props.type != "nonAnchorUngraded") {
      return Object.entries(measurement.values).map(([key, value]) => {
        return {
          id: key,
          baseSize: value.baseSize,
          displayName: value.displayName,
        };
      });
    } else {
      const min = measurement.values["min"];
      const max = measurement.values["max"];
      const step = measurement.values["step"];
      const result = [];

      for (let i = min; i <= max; i += step) {
        result.push(i);
      }
      return result.map((value) => {
        const fixedValue = parseFloat(parseFloat(`${value}`).toFixed(2));
        return {
          id: handleize(`${fixedValue}`),
          value: fixedValue,
          baseSize: `${fixedValue}`,
          displayName: measurement.displayNameTemplate.replace("{{1}}", value),
        };
      });
    }
  };

  const selectedDisplayName = (measurement) => {
    if (props.type != "nonAnchorUngraded") {
      return measurement.values[measurement.selected].displayName[
        props.fit().selected
      ];
    } else {
      return measurement.selected != ""
        ? measurement.displayNameTemplate.replace(
          "{{1}}",
          measurement.selected.replace("-", ".")
        )
        : "";
    }
  };

  return (
    <For each={props.measurements}>
      {(measurement, index) => (
        <div class="w-full pt-2 px-2">
          <span className="font-bold">
            {measurement.label !== "" ? measurement.label : "Size"}:{" "}
          </span>
          <span className="text-xs font-regular">
            {selectedDisplayName(measurement)}
          </span>

          <div className="grid grid-cols-5 sm:grid-cols-6 gap-1 flex-wrap mt-2">
            <For each={valueOptions(measurement)}>
              {(value) => (
                <NeumorphicCustomizationButton
                  selected={value.id === measurement.selected}
                  onClick={() => props.handleChange(measurement.id, value.id)}
                  label={value.baseSize}
                />
              )}
            </For>
          </div>
        </div>
      )}
    </For>
  );
};

const BottomCTABar = (props) => {
  const {
    sizeSettings: { standardSizeData },
    fit: { fit },
  } = useHeroForm();

  return (
    <div class="fixed flex flex-col bottom-0 left-0 right-0 bg-canvas border-frame border-t-2 z-20 ">
      <div class="flex flex-wrap gap-1 pt-0.5 justify-center text-sm w-full">
        <For each={Object.values(standardSizeData().measurements.anchorGraded)}>
          {(measurement) => (
            <span class="font-bold">
              {measurement.label != "" ? measurement.label : "Size"}:{" "}
              <span class="font-regular">
                {measurement.values[measurement.selected].baseSize}
              </span>
            </span>
          )}
        </For>

        <For
          each={Object.values(
            standardSizeData().measurements.nonAnchorUngraded
          )}
        >
          {(measurement) => (
            <Show when={measurement.selected != ""}>
              <span class="font-bold">
                {measurement.label != "" ? measurement.label : "Size"}:{" "}
                <span class="font-regular">
                  {measurement.displayNameTemplate.replace(
                    "{{1}}",
                    measurement.selected.replace("-", ".")
                  )}
                </span>
              </span>
            </Show>
          )}
        </For>
        <span class="font-bold">
          {" "}
          {fit().label}:{" "}
          <span className="font-regular">
            {" "}
            {fit().options[fit().selected].displayName || fit().options[fit().selected].label}{" "}
          </span>{" "}
        </span>
      </div>
      <div class="grid grid-cols-3 gap-1 p-1 w-full ">
        <button
          onClick={() => props.handleModalOpenState(false)}
          type="button"
          class="py-2 px-2 flex-grow text-base border font-bold flex justify-center items-center rounded-lg max-w-sm border-ink text-ink bg-canvas-light hover:bg-canvas-lighter"
        >Save Size
        </button>
        <AddToBagButton
          id="size-selector-add-to-cart"
          class="col-span-2"
          buttonClass="bg-ink"
          handleAddToBagClick={async () => {
            const flag = await window.handleAddToBagClick("addToCart");
            props.handleModalOpenState(false);
            if (!flag) return;
            document.body.style.overflow = "hidden";
          }}
        />


      </div>
    </div>
  );
};

export const StandardSizeSelectorWithGuide = (props) => {
  const {
    sizeSettings: {
      standardSizeData,
      handlers: { handleAnchorGradedChange, handleNonAnchorUngradedChange },
    },
    sizeSettings,
    fit: {
      fit,
      handlers: { handleFitSelection },
    },
  } = useHeroForm();

  let styleGuideElement;
  let scrollObserver;

  const [modalState, setModalState] = createSignal(false);
  const data = props.data;

  const handleModalOpenState = (state) => {
    const body = document.getElementsByTagName("body")[0];
    body.style.overflow = state ? "hidden" : "";

    setModalState(state);
  };

  onCleanup(() => {
    document.body.style.overflow = "";
  });

  onMount(() => {

    // Select a good default inseam/ nonAnchorUngraded if they are empty
    for (const measurement of Object.values(
      standardSizeData().measurements.nonAnchorUngraded
    )) {
      if (measurement.selected === "") {
        const selectedId = Object.values(
          standardSizeData().measurements.anchorGraded
        ).shift().selected;
        try {
          const defaultSize =
            data.fits[fit().selected].sizes[selectedId].measurements[
              measurement.id
            ].value;
          handleNonAnchorUngradedChange(measurement.id, String(defaultSize));
        } catch (error) {
          console.error(error);
        }
      }
    }
  });

  const createScrollListener = (testElement) => {
    scrollObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          styleGuideElement.style.maxHeight = '100%';
          styleGuideElement.classList.remove('brightness-95');
        }
      })
    }, {
      threshold: 1.0
    });

    scrollObserver.observe(testElement);
  }

  const fits = data.fits;

  const [selectedFit, setSelectedFit] = createSignal(Object.keys(fits)[0]);
  const [fitData, selectedFitData] = createSignal(fits[Object.keys(fits)[0]]);
  const [selectedUnit, setSelectedUnit] = createSignal(data.unit);
  const handleFitChange = (key) => {
    setSelectedFit(key);
    selectedFitData(fits[key]);

    handleFitSelection(key);
  };

  return (
    <div 
      // class="flex flex-col w-full gap-1 items-end"
    >
      <div
        onClick={() => handleModalOpenState(true)}
        class="flex w-full justify-between p-1 items-center relative bg-canvas bg-no-repeat cursor-pointer min-w-25 sm:min-w-30 border-outer border-solid border-frame"
      >
        <div>
          <span className=" font-medium text-sm">Selected Size</span>
          <div class="flex flex-col ">
            <For
              each={Object.values(standardSizeData().measurements.anchorGraded)}
            >
              {(measurement) => (
                <span class="font-medium text-xs">
                  {measurement.label != "" ? measurement.label : "Size"}:{" "}
                  <span class="font-regular">
                    {measurement.values[measurement.selected].baseSize}
                  </span>
                </span>
              )}
            </For>

            <For
              each={Object.values(
                standardSizeData().measurements.nonAnchorUngraded
              )}
            >
              {(measurement) => (
                <Show when={measurement.selected != ""}>
                  <span class="font-medium text-xs">
                    {measurement.label != "" ? measurement.label : "Size"}:{" "}
                    <span class="font-regular">
                      {measurement.displayNameTemplate.replace(
                        "{{1}}",
                        measurement.selected.replace("-", ".")
                      )}
                    </span>
                  </span>
                </Show>
              )}
            </For>
            <span class="font-medium text-xs">
              {fit().label}:{" "}
              <span className="font-regular">
                {fit().options[fit().selected].displayName || fit().options[fit().selected].label}{" "}
              </span>
            </span>
          </div>
        </div>
        <svg
          class="-rotate-90"
          height="12"
          viewBox="0 0 26 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13 21L0.875647 -2.28386e-06L25.1244 -1.63974e-07L13 21Z"
            fill="#2B190F"
          />
        </svg>
      </div>

      <div
        class="cursor-pointer text-left w-max text-sm text-ink-link hover:text-secondary-400 mt-2"
        onClick={() => handleModalOpenState(true)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="size-2 inline-block align-text-bottom"
          viewBox="0 -960 960 960"
        >
          <path
            stroke="currentColor"
            fill="currentColor"
            d="M444-288h72v-240h-72v240Zm35.789-312Q495-600 505.5-610.289q10.5-10.29 10.5-25.5Q516-651 505.711-661.5q-10.29-10.5-25.5-10.5Q465-672 454.5-661.711q-10.5 10.29-10.5 25.5Q444-621 454.289-610.5q10.29 10.5 25.5 10.5Zm.487 504Q401-96 331-126q-70-30-122.5-82.5T126-330.958q-30-69.959-30-149.5Q96-560 126-629.5t82.5-122Q261-804 330.958-834q69.959-30 149.5-30Q560-864 629.5-834t122 82.5Q804-699 834-629.276q30 69.725 30 149Q864-401 834-331q-30 70-82.5 122.5T629.276-126q-69.725 30-149 30ZM480-168q130 0 221-91t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z"
          />
        </svg>
        &thinsp; Size Guide
      </div>

      <ModalV2
        title={"Select a size"}
        modalState={modalState()}
        handleModalOpenState={handleModalOpenState}
      >
        <div class="flex flex-col items-stretch w-full relative ">
          <BottomCTABar handleModalOpenState={handleModalOpenState} />
          <div class="flex flex-col gap-2 p-2">
            <FitSelector fit={fit} handleChange={handleFitChange} />

            <SizeRadioSelector
              measurements={Object.values(
                standardSizeData()["measurements"]["anchorGraded"]
              )}
              type="anchorGraded"
              handleChange={handleAnchorGradedChange}
              fit={fit}
            />

            <SizeRadioSelector
              measurements={Object.values(
                standardSizeData()["measurements"]["nonAnchorUngraded"]
              )}
              type="nonAnchorUngraded"
              handleChange={handleNonAnchorUngradedChange}
              fit={fit}
            />


          </div>
          <div>



            <div ref={styleGuideElement} class="relative p-2 pb-16 border-frame border-t-outer bg-canvas-depressed transition-all duration-700 brightness-95"
              style="max-height: 25dvh;"
            >
              <div ref={(testElement) => { createScrollListener(testElement) }} style="top: 25dvh;" class="absolute z-50 pointer-events-none bg-transparent h-3 w-1/2 max-h-[25dvh]  "></div>
              <ContextualSizeChart
                headers={data.headers}
                selectedUnit={selectedUnit}
                setSelectedUnit={setSelectedUnit}
                originalUnit={data.unit}
                fitdata={data.fits}
              />
              <Show when={fitData().description}>
                <div className="pt-2" innerHTML={fitData().description} />
              </Show>
            </div>
          </div>
        </div>
      </ModalV2>
    </div>
  );
};

export const StandardSizeContainer = (props) => {
  const {
    sizeSettings: {
      standardSizeData,
      handlers: { handleAnchorGradedChange, handleNonAnchorUngradedChange },
    },
    sizeSettings,
    fit: {
      fit,
      handlers: { handleFitSelection },
    },
  } = useHeroForm();

  let styleGuideElement;
  let scrollObserver;

  const [modalState, setModalState] = createSignal(false);
  const data = props.data;

  const handleModalOpenState = (state) => {
    const body = document.getElementsByTagName("body")[0];
    body.style.overflow = state ? "hidden" : "";

    setModalState(state);
  };

  onCleanup(() => {
    document.body.style.overflow = "";
  });

  onMount(() => {

    // Select a good default inseam/ nonAnchorUngraded if they are empty
    for (const measurement of Object.values(
      standardSizeData().measurements.nonAnchorUngraded
    )) {
      if (measurement.selected === "") {
        const selectedId = Object.values(
          standardSizeData().measurements.anchorGraded
        ).shift().selected;
        try {
          const defaultSize =
            data.fits[fit().selected].sizes[selectedId].measurements[
              measurement.id
            ].value;
          handleNonAnchorUngradedChange(measurement.id, String(defaultSize));
        } catch (error) {
          console.error(error);
        }
      }
    }
  });

  const createScrollListener = (testElement) => {
    scrollObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          styleGuideElement.style.maxHeight = '100%';
          styleGuideElement.classList.remove('brightness-95');
        }
      })
    }, {
      threshold: 1.0
    });

    scrollObserver.observe(testElement);
  }

  const fits = data.fits;

  const [selectedFit, setSelectedFit] = createSignal(Object.keys(fits)[0]);
  const [fitData, selectedFitData] = createSignal(fits[Object.keys(fits)[0]]);
  const [selectedUnit, setSelectedUnit] = createSignal(data.unit);
  const handleFitChange = (key) => {
    setSelectedFit(key);
    selectedFitData(fits[key]);

    handleFitSelection(key);
  };

  return (
    <div
    // class="flex flex-col w-full gap-1 items-end"
    >
      <div class="divide-y divide-frame flex flex-col gap-3 py-3">
        <FitSelectorMainForm fit={fit} handleChange={handleFitChange} handleSizeGuideOpen={handleModalOpenState} />

        <SizeRadioSelectorMainForm
          measurements={Object.values(
            standardSizeData()["measurements"]["anchorGraded"]
          )}
          type="anchorGraded"
          handleChange={handleAnchorGradedChange}
          fit={fit}
        />

        <SizeRadioSelectorMainForm
          measurements={Object.values(
            standardSizeData()["measurements"]["nonAnchorUngraded"]
          )}
          type="nonAnchorUngraded"
          handleChange={handleNonAnchorUngradedChange}
          fit={fit}
        />
      </div>

      <ModalV2
        title={"Select a size"}
        modalState={modalState()}
        modalClass="h-full"
        handleModalOpenState={handleModalOpenState}
      >
        <div class="flex flex-col items-stretch w-full relative ">
          <BottomCTABar handleModalOpenState={handleModalOpenState} />
          <div class="flex flex-col gap-2 p-2">
            <FitSelector fit={fit} handleChange={handleFitChange} />

            <SizeRadioSelector
              measurements={Object.values(
                standardSizeData()["measurements"]["anchorGraded"]
              )}
              type="anchorGraded"
              handleChange={handleAnchorGradedChange}
              fit={fit}
            />

            <SizeRadioSelector
              measurements={Object.values(
                standardSizeData()["measurements"]["nonAnchorUngraded"]
              )}
              type="nonAnchorUngraded"
              handleChange={handleNonAnchorUngradedChange}
              fit={fit}
            />


          </div>
          <div>



            <div ref={styleGuideElement} class="relative p-2 pb-16 border-frame border-t-outer bg-canvas-depressed transition-all duration-700 brightness-95"
              style="max-height: 25dvh;"
            >
              <div ref={(testElement) => { createScrollListener(testElement) }} style="top: 25dvh;" class="absolute z-50 pointer-events-none bg-transparent h-3 w-1/2 max-h-[25dvh]  "></div>
              <ContextualSizeChart
                headers={data.headers}
                selectedUnit={selectedUnit}
                setSelectedUnit={setSelectedUnit}
                originalUnit={data.unit}
                fitdata={data.fits}
              />
              <Show when={fitData().description}>
                <div className="pt-2" innerHTML={fitData().description} />
              </Show>
            </div>
          </div>
        </div>
      </ModalV2>
    </div>
  );
};
